"use client";

import { FC, useEffect, useRef, useState } from "react";
import {
  getAllDocumentsByProjectIdAction,
  searchProjectDocumentsAction,
} from "@/app/_lib/actions/document.actions";
import CloudImage from "@/app/_lib/components/cloudinary/cloud-image";
import { DocumentTypes } from "@/prisma-package/src";
import { documentTypeLabel } from "@/app/_lib/utils/document-category.utils";

export type PickerDocument = {
  id: string;
  publicId: string;
  secureUrl: string;
  type: DocumentTypes;
};

type Props = {
  projectId: string;
  attachedDocumentIds: string[];
  onSelect: (doc: PickerDocument) => void;
};

// The content search returns raw Mongo documents (_id.$oid instead of id)
const toPickerDocument = (doc: any): PickerDocument => ({
  id: doc.id ?? doc._id?.$oid,
  publicId: doc.publicId,
  secureUrl: doc.secureUrl,
  type: doc.type as DocumentTypes,
});

const DocumentPicker: FC<Props> = ({
  projectId,
  attachedDocumentIds,
  onSelect,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<DocumentTypes | "">("");
  const [documents, setDocuments] = useState<PickerDocument[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const query = searchTerm.trim();
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const [error, results] = query
          ? await searchProjectDocumentsAction({
              projectId,
              query,
              documentType: typeFilter || undefined,
            })
          : await getAllDocumentsByProjectIdAction({
              projectId,
              documentType: typeFilter || undefined,
            });

        if (error || !results) {
          setDocuments([]);
          return;
        }

        setDocuments(results.map(toPickerDocument).filter((doc) => doc.id));
      } finally {
        setIsSearching(false);
      }
    }, query ? 300 : 0);

    return () => clearTimeout(timer);
  }, [isOpen, searchTerm, typeFilter, projectId]);

  return (
    <>
      <button
        type="button"
        className="btn btn-md btn-outline btn-primary"
        onClick={() => {
          setIsOpen(true);
          dialogRef.current?.showModal();
        }}
      >
        Choose Existing Document
      </button>
      <dialog ref={dialogRef} className="modal" onClose={() => setIsOpen(false)}>
        <div className="modal-box w-11/12 max-w-3xl">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <h3 className="font-bold text-lg mb-4">Choose Document</h3>
          <div className="flex items-center gap-4 mb-4">
            <input
              type="text"
              className="input input-bordered input-sm flex-1"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              title="Filter by type"
              className="select select-bordered select-sm"
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as DocumentTypes | "")
              }
            >
              <option value="">All types</option>
              {Object.values(DocumentTypes).map((type) => (
                <option key={type} value={type}>
                  {documentTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
          {isSearching ? (
            <div className="p-8 text-center">
              <span className="loading loading-spinner loading-sm" />
              <span className="ml-2">Searching documents...</span>
            </div>
          ) : documents.length === 0 ? (
            <div className="p-8 text-center text-base-content/60">
              No documents found
            </div>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Preview</th>
                    <th>Type</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => {
                    const isAttached = attachedDocumentIds.includes(doc.id);
                    return (
                      <tr key={doc.id}>
                        <td>
                          <CloudImage
                            src={doc.secureUrl}
                            width={80}
                            height={80}
                            alt={documentTypeLabel(doc.type)}
                            documentType={doc.type}
                            viewOnly
                          />
                        </td>
                        <td>{documentTypeLabel(doc.type)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-primary btn-xs"
                            disabled={isAttached}
                            onClick={() => onSelect(doc)}
                          >
                            {isAttached ? "Attached" : "Attach"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </>
  );
};

export default DocumentPicker;
